import { motion } from "framer-motion";
import { ShieldCheck, Lock, Database, Code, Users, Bell, Clock, Shield } from "@phosphor-icons/react";

export function Security() {
  const securityFeatures = [
    {
      icon: ShieldCheck,
      title: "Enterprise-Grade Security",
      description:
        "Our infrastructure is built on AWS with SOC 2 Type II compliance, regular third-party audits, and industry-leading security practices.",
    },
    {
      icon: Lock,
      title: "Advanced Authentication",
      description:
        "Enterprise SSO (Okta, Azure AD), mandatory 2FA, and role-based access control (RBAC) to ensure secure access management.",
    },
    {
      icon: Database,
      title: "Data Protection",
      description:
        "AES-256 encryption at rest and TLS 1.3 in transit. GDPR and CCPA compliant with data processing agreements available.",
    },
    {
      icon: Code,
      title: "Secure Code Analysis",
      description:
        "Isolated execution environments with ephemeral containers. Your code never persists beyond analysis completion.",
    },
    {
      icon: Users,
      title: "Access Controls",
      description:
        "Fine-grained permissions, audit logs, and IP allowlisting. Control exactly who can access what within your organization.",
    },
    {
      icon: Bell,
      title: "24/7 Monitoring",
      description:
        "Real-time security monitoring, automated threat detection, and incident response procedures with < 1 hour SLA.",
    },
    {
      icon: Clock,
      title: "Business Continuity",
      description:
        "Automated backups, disaster recovery with RPO < 1 hour and RTO < 4 hours, and multi-region failover capability.",
    },
    {
      icon: Shield,
      title: "Compliance & Privacy",
      description:
        "GDPR, CCPA, and HIPAA compliant. Annual penetration testing and vulnerability assessments by leading security firms.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#03001C] to-[#090024]">
      <main className="container mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent mb-8 text-center">
            Enterprise-Grade Security
          </h1>

          <p className="text-xl text-white/80 text-center mb-16">
            Security is our top priority. Our platform is built with bank-level security measures,
            compliance certifications, and comprehensive data protection to safeguard your code and data.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10"
              >
                <feature.icon
                  size={32}
                  weight="bold"
                  className="text-blue-400 mb-4"
                />
                <h3 className="text-xl font-display font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/70">{feature.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 space-y-8">
            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Compliance & Certifications
              </h2>
              <p className="text-white/80">
                We maintain the highest level of security compliance and undergo regular audits:
              </p>
              <ul className="list-disc pl-6 mt-4 text-white/80">
                <li>SOC 2 Type II certified</li>
                <li>ISO 27001 certified</li>
                <li>GDPR and CCPA compliant</li>
                <li>Annual third-party penetration testing</li>
                <li>Quarterly vulnerability assessments</li>
                <li>Bug bounty program with HackerOne</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Data Processing & Privacy
              </h2>
              <p className="text-white/80">
                We process data in accordance with international privacy laws and best practices:
              </p>
              <ul className="list-disc pl-6 mt-4 text-white/80">
                <li>Data processing agreements (DPA) available for enterprise customers</li>
                <li>30-day maximum data retention with configurable retention policies</li>
                <li>Data stored in your choice of AWS regions (US, EU, APAC)</li>
                <li>Right to erasure and data portability supported</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-display font-semibold text-white mb-4">
                Responsible Disclosure
              </h2>
              <p className="text-white/80">
                We value the security research community. If you believe you have found a security
                vulnerability, please report it to{" "}
                <a
                  href="mailto:security@aicodereview.dev"
                  className="text-blue-400 hover:text-blue-300"
                >
                  security@aicodereview.dev
                </a>
                . We offer competitive bounties for verified vulnerabilities.
              </p>
            </section>

            <section className="text-sm text-white/60">
              <p>Last updated: {new Date().toLocaleDateString()}</p>
            </section>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
